import { Dialog, Menu, Transition, Switch } from "@headlessui/react";
import {
  Bars3Icon,
  BellIcon,
  XMarkIcon,
} from "@heroicons/react/24/solid";
import { Fragment, useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useAuth } from "../hooks/auth";
import { useCanAccess } from "../hooks/useCanAccess";
import useNotification from "../hooks/useNotification";
import ChatBox from "@/components/Chat/chatbox";
import useChatNotification from "@/hooks/useChatNotification";
import Badge from "@/components/Badge";
import { DesktopNavbar, ProfileDropdown, NotificationMenu, MobileNavbar, NavSearch } from "@/components/layout";
import GlobalSearch from "@/components/GlobalSearch";


export default function Layout() {
  const { isAvailableForChat, toggleChatAvailability, isApplicant } = useAuth();
  const canAccess = useCanAccess();
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const [searchOpen, setSearchOpen] = useState<boolean>(false);
  const { notifications, unreadCount } = useNotification();
  const { pendingChats } = useChatNotification();

  // Global keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    if (isApplicant) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen((prev) => !prev);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isApplicant]);

  return (
    <>
      <div>
        <Transition.Root show={sidebarOpen} as={Fragment}>
          <Dialog
            as="div"
            className="fixed inset-0 flex z-[250] md:hidden"
            onClose={setSidebarOpen}
          >
            <Transition.Child
              as={Fragment}
              enter="transition-opacity ease-linear duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity ease-linear duration-300"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
            </Transition.Child>
            <Transition.Child
              as={Fragment}
              enter="transition ease-in-out duration-300 transform"
              enterFrom="-translate-x-full"
              enterTo="translate-x-0"
              leave="transition ease-in-out duration-300 transform"
              leaveFrom="translate-x-0"
              leaveTo="-translate-x-full"
            >
              <div className="relative flex-1 flex flex-col max-w-xs w-full pt-5 pb-4 bg-primary-700">
                <Transition.Child
                  as={Fragment}
                  enter="ease-in-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in-out duration-300"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="absolute top-0 right-0 -mr-12 pt-2">
                    <button
                      type="button"
                      className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                      onClick={() => setSidebarOpen(false)}
                    >
                      <span className="sr-only">Close sidebar</span>
                      <XMarkIcon
                        className="h-6 w-6 text-white"
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </Transition.Child>
                <div className="flex-shrink-0 flex items-center px-4">
                  <img
                    className="h-8 w-auto"
                    src="https://freesvg.org/img/student_hat_1.png"
                    alt="Consultancy"
                  />
                  <p className="font-extrabold text-white px-3 text-xl">
                    CAC Central
                  </p>
                </div>
                <div className="mt-5 flex-1 h-0 overflow-y-auto">
                  <MobileNavbar pendingChats={pendingChats} />
                </div>
              </div>
            </Transition.Child>
            <div className="flex-shrink-0 w-14" aria-hidden="true">
              {/* Dummy element to force sidebar to shrink to fit close icon */}
            </div>
          </Dialog>
        </Transition.Root>

        {/* Static sidebar for desktop */}
        <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0 z-[200]">
          <DesktopNavbar pendingChats={pendingChats} />
        </div>

        <div className="md:pl-64 flex flex-col flex-1">
          <div className="sticky top-0 flex-shrink-0 flex h-12 bg-white shadow z-50">
            {/* Mobile sidebar toggle */}
            <button
              type="button"
              className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 md:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <span className="sr-only">Open sidebar</span>
              <Bars3Icon className="h-6 w-6" aria-hidden="true" />
            </button>

            <div className="flex-1 px-4 flex justify-between items-center gap-4">
              {!isApplicant ? <NavSearch onOpen={() => setSearchOpen(true)} /> : <div />}

              {/* Right-side actions */}
              <div className="flex items-center gap-3">
                {canAccess({ permission: "menu-chat" }) && (
                  <div className="flex items-center">
                    <Switch
                      checked={isAvailableForChat}
                      onChange={toggleChatAvailability}
                      className={`${
                        isAvailableForChat ? "bg-primary-500" : "bg-gray-200"
                      } relative inline-flex h-6 w-11 items-center rounded-full transition-colors`}
                    >
                      <span className="sr-only">Toggle chat availability</span>
                      <span
                        className={`${
                          isAvailableForChat ? "translate-x-6" : "translate-x-1"
                        } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                      />
                    </Switch>
                    <span className="ml-2 text-sm text-gray-600 hidden lg:inline">
                      {isAvailableForChat ? "Available for Chat" : "Unavailable"}
                    </span>
                  </div>
                )}

                <Menu as="div" className="relative">
                  <Menu.Button className="relative bg-white p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500">
                    <span className="sr-only">View notifications</span>
                    <BellIcon className="h-6 w-6" aria-hidden="true" />
                    {unreadCount > 0 && (
                      <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-400 ring-2 ring-white" />
                    )}
                    {notifications?.length > 0 && (
                      <Badge>{notifications.length}</Badge>
                    )}
                  </Menu.Button>
                  <NotificationMenu />
                </Menu>

                <ProfileDropdown />
              </div>
            </div>
          </div>

          <main>
            <div className="max-w-full">
              <Outlet />
            </div>
          </main>
        </div>

        {canAccess({ permission: "use-chatbox" }) && <ChatBox />}
      </div>

      {!isApplicant && <GlobalSearch isOpen={searchOpen} onClose={() => setSearchOpen(false)} />}
    </>
  );
}
